import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { useTeacherTutorAnalytics } from '../../tutor/hooks/useTeacherTutorAnalytics';
import { TeacherTutorAggregateCards } from '../../components/teacher/tutor-analytics/TeacherTutorAggregateCards';
import { TeacherTutorStudentsTable } from '../../components/teacher/tutor-analytics/TeacherTutorStudentsTable';
import { BarChartCard } from '../../components/ai-tutor/analytics/BarChartCard';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { RefreshCw, Search, Users, BarChart3, RotateCcw, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import { getTeacherMyClasses, getTeacherMyStudents } from '../../modules/roster/api/teacher.client';
import type { TeacherClass } from '../../modules/roster/api/teacher.client';
import type { TeacherStudentEntry } from '../../modules/roster/api/teacher.client';
import type { TeacherStudentSummaryItem } from '../../tutor/types/teacherTutorAnalytics';
import { exportElementToPdf } from '../../utils/pdfExport';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandler';

const GRADE_OPTIONS = [
  { value: '', label: 'All grades' },
  ...Array.from({ length: 12 }, (_, i) => i + 1).map((g) => ({
    value: String(g),
    label: `Grade ${g}`,
  })),
];

const PAGE_SIZE_OPTIONS = [
  { value: '0', label: 'All' },
  { value: '10', label: '10 per page' },
  { value: '25', label: '25 per page' },
  { value: '50', label: '50 per page' },
];

export const TeacherTutorAnalyticsPage: React.FC = () => {
  const [classes, setClasses] = useState<TeacherClass[]>([]);
  const [students, setStudents] = useState<TeacherStudentEntry[]>([]);
  const [rosterLoading, setRosterLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setRosterLoading(true);
    Promise.all([getTeacherMyClasses(), getTeacherMyStudents()])
      .then(([c, s]) => {
        if (!cancelled) {
          setClasses(Array.isArray(c) ? c : []);
          setStudents(Array.isArray(s) ? s : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setClasses([]);
          setStudents([]);
        }
      })
      .finally(() => {
        if (!cancelled) setRosterLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    data,
    loading,
    error,
    days,
    setDays,
    refetch,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    studentId,
    setStudentId,
    classId,
    setClassId,
    grade,
    setGrade,
    skip,
    setSkip,
    limit,
    setLimit,
    dateRangeError,
    totalFromResponse,
    resetFilters,
    searchTerm,
    setSearchTerm,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    filteredAndSortedData,
  } = useTeacherTutorAnalytics({ initialDays: 30 });

  const dayOptions = [7, 14, 30, 90];

  const classOptions = useMemo(
    () => [
      { value: '', label: 'All classes' },
      ...classes.map((c) => ({ value: c.id, label: c.name || c.id })),
    ],
    [classes]
  );

  const studentOptions = useMemo(() => {
    const list = students.filter((e) => e.student.platform_user_id);
    return [
      { value: '', label: 'All students' },
      ...list.map((e) => ({
        value: e.student.platform_user_id!,
        label:
          e.student.display_name ||
          [e.student.first_name, e.student.last_name].filter(Boolean).join(' ') ||
          e.student.admission_no ||
          e.student.platform_user_id!,
      })),
    ];
  }, [students]);

  const total = totalFromResponse ?? data?.summaries?.length ?? 0;
  const totalPages = limit > 0 ? Math.ceil(total / limit) : 1;
  const currentPage = limit > 0 ? Math.floor(skip / limit) + 1 : 1;
  const hasPagination = limit > 0 && totalPages > 1;

  const [isExporting, setIsExporting] = useState(false);
  const reportContainerRef = useRef<HTMLDivElement>(null);

  const minutesByStudent = useMemo(() => {
    const list = filteredAndSortedData.slice(0, 15);
    return list.map((item) => ({
      label: item.student_info?.display_name || item.student_id || '—',
      value: item.summary?.total_tutor_minutes ?? 0,
    }));
  }, [filteredAndSortedData]);

  const sessionsByStudent = useMemo(() => {
    const list = filteredAndSortedData.slice(0, 15);
    return list.map((item) => ({
      label: item.student_info?.display_name || item.student_id || '—',
      value: item.summary?.sessions_count ?? 0,
    }));
  }, [filteredAndSortedData]);

  const scoreDistribution = useMemo(() => {
    const buckets = [
      { label: '0-25%', min: 0, max: 25 },
      { label: '26-50%', min: 26, max: 50 },
      { label: '51-75%', min: 51, max: 75 },
      { label: '76-100%', min: 76, max: 100 },
    ];
    return buckets.map((b) => ({
      label: b.label,
      value: filteredAndSortedData.filter(
        (item) =>
          item.summary?.latest_overall_score != null &&
          item.summary.latest_overall_score >= b.min &&
          item.summary.latest_overall_score <= b.max
      ).length,
    }));
  }, [filteredAndSortedData]);

  const scoreByStudent = useMemo(() => {
    const list = filteredAndSortedData.slice(0, 15);
    return list
      .filter((item) => item.summary?.latest_overall_score != null)
      .map((item) => ({
        label: item.student_info?.display_name || item.student_id || '—',
        value: item.summary!.latest_overall_score!,
      }));
  }, [filteredAndSortedData]);

  useEffect(() => {
    if (!isExporting || !reportContainerRef.current) return;
    const timer = setTimeout(() => {
      if (!reportContainerRef.current) {
        setIsExporting(false);
        return;
      }
      exportElementToPdf(reportContainerRef.current, {
        filename: 'teacher-tutor-analytics-summary',
        delayBeforeCapture: 0,
      })
        .then(() => showSuccessToast('Report downloaded'))
        .catch((err) => showErrorToast(err instanceof Error ? err : String(err)))
        .finally(() => setIsExporting(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [isExporting]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (loading && !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <PageHeader title="AI Tutor Analytics" subtitle="Monitor student progress and performance" />
          <div className="animate-pulse space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-24"></div>
              ))}
            </div>
            <div className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-96"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="space-y-6">
          <PageHeader title="AI Tutor Analytics" subtitle="Monitor student progress and performance" />
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        </div>
      </Layout>
    );
  }

  if (!data && !dateRangeError) {
    return (
      <Layout>
        <div className="space-y-6">
          <PageHeader title="AI Tutor Analytics" subtitle="Monitor student progress and performance" />
          <Alert variant="error" className="mb-6">
            Failed to load analytics data
          </Alert>
        </div>
      </Layout>
    );
  }

  const displayData = data ?? {
    ok: true,
    days: 30,
    total_students: 0,
    summaries: [],
    aggregate: {
      total_tutor_minutes: 0,
      total_sessions: 0,
      avg_overall_score: null,
      speaking_level_distribution: {},
    },
  };

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="AI Tutor Analytics" subtitle="Monitor student progress and performance" />

        {/* Controls */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Analytics Controls
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {dateRangeError && (
              <Alert variant="error" className="mb-2">
                {dateRangeError}
              </Alert>
            )}
            <div className="flex flex-wrap gap-4 items-end">
              {/* Period (days) */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Period:</span>
                <div className="flex gap-1">
                  {dayOptions.map((option) => (
                    <Button
                      key={option}
                      variant={days === option ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => setDays(option)}
                      className="px-3 py-1 text-xs"
                    >
                      {option}d
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom date range */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">From:</span>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">To:</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
              </div>

              {/* Student filter */}
              <div className="min-w-[160px]">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Student
                </label>
                <Select
                  options={rosterLoading ? [{ value: '', label: 'Loading...' }] : studentOptions}
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  disabled={rosterLoading}
                />
              </div>

              {/* Class filter */}
              <div className="min-w-[160px]">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Class
                </label>
                <Select
                  options={rosterLoading ? [{ value: '', label: 'Loading...' }] : classOptions}
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                  disabled={rosterLoading}
                />
              </div>

              {/* Grade filter */}
              <div className="min-w-[120px]">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Grade
                </label>
                <Select
                  options={GRADE_OPTIONS}
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                />
              </div>

              {/* Page size */}
              <div className="min-w-[120px]">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Per page
                </label>
                <Select
                  options={PAGE_SIZE_OPTIONS}
                  value={String(limit)}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setLimit(n);
                    if (n > 0) setSkip(0);
                  }}
                />
              </div>

              {/* Search (client-side) */}
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <Input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48"
                />
              </div>

              <div className="flex gap-2 ml-auto">
                <Button onClick={resetFilters} variant="outline" size="sm">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset filters
                </Button>
                <Button
                  onClick={() => setIsExporting(true)}
                  disabled={isExporting}
                  variant="outline"
                  size="sm"
                >
                  {isExporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  {isExporting ? 'Preparing…' : 'Export report'}
                </Button>
                <Button onClick={refetch} disabled={loading} variant="secondary" size="sm">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Aggregate Cards */}
        <TeacherTutorAggregateCards data={displayData} />

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BarChartCard
            title="Tutor minutes by student"
            subtitle="Top 15 students"
            data={minutesByStudent}
            getLabel={(d) => d.label}
            getValue={(d) => d.value}
            valueFormat={(v) => `${v} min`}
            emptyMessage="No student data"
          />
          <BarChartCard
            title="Sessions by student"
            subtitle="Top 15 students"
            data={sessionsByStudent}
            getLabel={(d) => d.label}
            getValue={(d) => d.value}
            valueFormat={(v) => `${v} sessions`}
            emptyMessage="No session data"
          />
          <BarChartCard
            title="Score distribution"
            subtitle="Students by score range (latest %)"
            data={scoreDistribution}
            getLabel={(d) => d.label}
            getValue={(d) => d.value}
            valueFormat={(v) => `${v} students`}
            emptyMessage="No score data"
          />
          <BarChartCard
            title="Average score by student"
            subtitle="Top 15 students (latest score %)"
            data={scoreByStudent}
            getLabel={(d) => d.label}
            getValue={(d) => d.value}
            valueFormat={(v) => `${v}%`}
            emptyMessage="No score data"
          />
        </div>

        {/* Students Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Student Analytics ({filteredAndSortedData.length} students)
              {limit > 0 && totalFromResponse != null && (
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  {' '}
                  (page {currentPage} of {totalPages})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <TeacherTutorStudentsTable
              students={filteredAndSortedData}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            {hasPagination && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkip(skip + limit)}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {isExporting && (
          <div
            ref={reportContainerRef}
            style={{ position: 'fixed', left: '-9999px', top: 0, zIndex: -1, width: 794 }}
            aria-hidden
          >
            <div
              className="bg-white text-gray-900 p-6"
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              <h1 className="text-xl font-bold mb-2">AI Tutor Analytics – Teacher Summary</h1>
              <p className="text-sm text-gray-600 mb-4">
                {startDate && endDate
                  ? `Period: ${startDate} to ${endDate}`
                  : `Last ${days} days`}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                <div><strong>Total students</strong><br />{displayData.total_students ?? 0}</div>
                <div><strong>Total minutes</strong><br />{displayData.aggregate?.total_tutor_minutes ?? 0} min</div>
                <div><strong>Total sessions</strong><br />{displayData.aggregate?.total_sessions ?? 0}</div>
                <div><strong>Avg score</strong><br />{displayData.aggregate?.avg_overall_score != null ? `${displayData.aggregate.avg_overall_score}%` : '—'}</div>
              </div>
              {Object.keys(displayData.aggregate?.speaking_level_distribution ?? {}).length > 0 && (
                <div className="mb-4">
                  <strong className="text-sm">Speaking levels:</strong>
                  <ul className="text-sm list-disc list-inside">
                    {Object.entries(displayData.aggregate!.speaking_level_distribution).map(([k, v]) => (
                      <li key={k}>{k}: {v}</li>
                    ))}
                  </ul>
                </div>
              )}
              <table className="w-full text-sm border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-2 text-left">Student</th>
                    <th className="border border-gray-300 p-2 text-right">Minutes</th>
                    <th className="border border-gray-300 p-2 text-right">Sessions</th>
                    <th className="border border-gray-300 p-2 text-left">Level</th>
                    <th className="border border-gray-300 p-2 text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {(displayData.summaries ?? []).slice(0, 50).map((s) => (
                    <tr key={s.student_id}>
                      <td className="border border-gray-300 p-2">{s.student_info?.display_name || s.student_id}</td>
                      <td className="border border-gray-300 p-2 text-right">{s.summary?.total_tutor_minutes ?? 0}</td>
                      <td className="border border-gray-300 p-2 text-right">{s.summary?.sessions_count ?? 0}</td>
                      <td className="border border-gray-300 p-2">{s.summary?.latest_speaking_level ?? '—'}</td>
                      <td className="border border-gray-300 p-2 text-right">{s.summary?.latest_overall_score != null ? `${s.summary.latest_overall_score}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(displayData.summaries?.length ?? 0) > 50 && (
                <p className="text-xs text-gray-500 mt-2">Showing first 50 of {displayData.summaries!.length} students.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};